;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();import type { JwtUser } from '@workspace/ui/types';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

const NPS_DISMISSAL_STORAGE_KEY = 'nps_dismissed_at';
const NPS_DISMISSAL_DURATION_DAYS = 14;

/**
 * Check if dismissal timestamp is still valid (within 14 days)
 */
function isDismissalValid(dismissedAt: string | null): boolean {
  if (!dismissedAt) return false;
  try {
    const dismissedDate = new Date(dismissedAt);
    const now = new Date();
    const daysSinceDismissal = (now.getTime() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24);
    return daysSinceDismissal < NPS_DISMISSAL_DURATION_DAYS;
  } catch {
    return false;
  }
}

/**
 * Load dismissal timestamp from localStorage
 */
function loadDismissedAt(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return localStorage.getItem(NPS_DISMISSAL_STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Save dismissal timestamp to localStorage
 */
function saveDismissedAt(timestamp: string): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(NPS_DISMISSAL_STORAGE_KEY, timestamp);
  } catch {
    // Ignore localStorage errors (e.g., quota exceeded, private browsing)
  }
}

/**
 * Clear dismissal timestamp from localStorage
 */
function clearDismissedAt(): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(NPS_DISMISSAL_STORAGE_KEY);
  } catch {
    // Ignore localStorage errors
  }
}

interface NpsState {
  /** Whether we have synced from the auth token (user loaded) */
  isLoaded: boolean;
  /** Whether the user has completed NPS (from token has_completed_nps or local markCompleted) */
  isCompleted: boolean;
  /** Whether the user dismissed the modal (checks localStorage for 14-day persistence) */
  isDismissed: boolean;
  /** ISO timestamp of when the survey was completed (if available) */
  completedAt: string | undefined;

  /** Sync NPS state from the access token (user.has_completed_nps). Call when user is available. */
  syncFromAuth: (user: JwtUser | null) => void;
  /** Mark survey as completed (after in-app submission) */
  markCompleted: () => void;
  /** Mark modal as dismissed and save to localStorage for 14 days */
  markDismissed: () => void;
  /** Reset store state (call on logout so next user gets a fresh state) */
  reset: () => void;
}

export const useNpsStore = create<NpsState>()(
  devtools(
    (set) => {
      // Initialize dismissal state from localStorage
      const dismissedAt = loadDismissedAt();
      const initialIsDismissed = isDismissalValid(dismissedAt);

      return {
        isLoaded: false,
        isCompleted: false,
        isDismissed: initialIsDismissed,
        completedAt: undefined,

        syncFromAuth: (user) => {
          if (!user) return;
          const isCompleted = user.has_completed_nps ?? false;

          // Re-check dismissal state from localStorage when syncing
          const currentDismissedAt = loadDismissedAt();
          const currentIsDismissed = isDismissalValid(currentDismissedAt);

          set(
            {
              isLoaded: true,
              isCompleted,
              isDismissed: currentIsDismissed,
            },
            undefined,
            'syncFromAuth'
          );
        },

        markCompleted: () => {
          // Clear dismissal when completed
          clearDismissedAt();
          set(
            { isCompleted: true, completedAt: new Date().toISOString(), isDismissed: false },
            undefined,
            'markCompleted'
          );
        },

        markDismissed: () => {
          const timestamp = new Date().toISOString();
          saveDismissedAt(timestamp);
          set({ isDismissed: true }, undefined, 'markDismissed');
        },

        reset: () => {
          clearDismissedAt();
          set(
            {
              isLoaded: false,
              isCompleted: false,
              isDismissed: false,
              completedAt: undefined,
            },
            undefined,
            'reset'
          );
        },
      };
    },
    {
      enabled: process.env.NODE_ENV === 'development',
      name: 'nps-store',
    }
  )
);
